import React, { useState } from "react";

interface ProfileAvatarProps {
  empName: string;
  empImage?: string;
  empId?: string;
  size?: "sm" | "md" | "lg" | "xl";
  showName?: boolean;
  showEmpId?: boolean;
  className?: string;
}

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  empName,
  empImage,
  empId,
  size = "md",
  showName = true,
  showEmpId = true,
  className = "",
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const displayName =
    empName && empName !== "N/A" && empName.trim() ? empName : "Unknown Employee";
  const initials =
    displayName !== "Unknown Employee"
      ? displayName
          .split(" ")
          .map((n) => (n && n[0] ? n[0] : ""))
          .join("")
          .substring(0, 2)
          .toUpperCase()
      : "UE";

  const showInitials = !empImage || imageError || !imageLoaded;

  // Convert empId to integer for display
  const numericEmpId = empId ? parseInt(empId, 10) : null;
  const displayEmpId =
    numericEmpId && !isNaN(numericEmpId) ? numericEmpId.toString() : empId || "";

  // Size configurations
  const sizeConfig = {
    sm: {
      width: "32px",
      height: "32px",
      fontSize: "12px",
    },
    md: {
      width: "40px",
      height: "40px",
      fontSize: "14px",
    },
    lg: {
      width: "48px",
      height: "48px",
      fontSize: "16px",
    },
    xl: {
      width: "64px",
      height: "64px",
      fontSize: "20px",
    },
  };

  const currentSize = sizeConfig[size];

  // Consistent avatar styles
  const avatarStyles: React.CSSProperties = {
    width: currentSize.width,
    height: currentSize.height,
    minWidth: currentSize.width,
    minHeight: currentSize.height,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  };

  const imageStyles: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  };

  const initialsStyles: React.CSSProperties = {
    ...avatarStyles,
    backgroundColor: "#0d6efd",
    color: "white",
    fontSize: currentSize.fontSize,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: "1",
  };

  return (
    <div className={`d-flex align-items-center gap-3 ${className}`}>
      <div style={avatarStyles}>
        {empImage && !imageError ? (
          <>
            <img
              src={`data:image/jpeg;base64,${empImage}`}
              alt={`${displayName}'s profile`}
              style={{
                ...imageStyles,
                display: showInitials ? "none" : "block",
              }}
              onError={() => {
                console.log("Image failed to load for employee:", empId);
                setImageError(true);
              }}
              onLoad={() => {
                console.log("Image loaded successfully for employee:", empId);
                setImageLoaded(true);
              }}
            />
            {showInitials && <div style={initialsStyles}>{initials}</div>}
          </>
        ) : (
          <div style={initialsStyles}>{initials}</div>
        )}
      </div>
      {(showName || showEmpId) && (
        <div className="flex-grow-1">
          {showName && (
            <div className="text-dark fw-medium mb-1">{displayName}</div>
          )}
          {showEmpId && empId && (
            <div className="text-muted small">ID: {displayEmpId}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileAvatar;
